import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ChevronLeft, ChevronRight, Shield, Truck, Headphones, Award, Star, Quote } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { KayakBadge } from '../components/KayakBadge';
import NewsletterSubscribe from '../components/NewsletterSubscribe';
import { SignupBanner } from '../components/SignupBanner';
import { FloatingAd } from '../components/FloatingAd';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Icon mapping for trust items
const iconMap = {
  shield: Shield,
  truck: Truck,
  headphones: Headphones,
  award: Award,
  check: Shield,
  star: Star
};

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  const [featuredScrollPosition, setFeaturedScrollPosition] = useState(0);
  const featuredRef = useRef(null);
  const divevolkRef = useRef(null);

  // Hero slides — first slide is fully admin-controlled (headline / subheading
  // / CTA all come from the Homepage Editor's "Hero Section"). Slides 2-4 are
  // section-preview slides that promote DiveVolk / Pre-Dived / Courses with
  // their own hardcoded copy. The hero images array from admin populates all
  // four slide backgrounds when provided.
  const adminHero = content?.hero || {};
  const heroSlides = [
    {
      headline: adminHero.headline || "Your phone is all the underwater camera you need",
      subheading: adminHero.subheading || "Professional underwater housings that turn your smartphone into a dive camera",
      cta_text: adminHero.cta_text || "Explore DiveVolk",
      cta_link: adminHero.cta_link || "/brand/divevolk",
      image: adminHero.images?.[0] || "https://customer-assets.emergentagent.com/job_3cfeeb3d-4da8-4519-ad3c-147d6848a7d7/artifacts/2s7i9rgn_WhatsApp%20Image%202026-04-03%20at%2021.14.55.jpeg"
    },
    {
      headline: "If it doesn't make sense, it doesn't belong here",
      subheading: "Every piece of gear we stock is tried, tested, and trusted by real divers",
      cta_text: "Shop New Gear",
      cta_link: "/products",
      image: adminHero.images?.[1] || "https://customer-assets.emergentagent.com/job_3cfeeb3d-4da8-4519-ad3c-147d6848a7d7/artifacts/crac6cgk_WhatsApp%20Image%202026-04-03%20at%2021.14.56.jpeg"
    },
    {
      headline: "Pre-Dived gear beautifully curated by divers for divers",
      subheading: "Quality second-hand equipment at unbeatable prices - inspected and ready to dive",
      cta_text: "Browse Pre-Dived",
      cta_link: "/pre-dived",
      image: adminHero.images?.[2] || "https://customer-assets.emergentagent.com/job_3cfeeb3d-4da8-4519-ad3c-147d6848a7d7/artifacts/p6tpjp65_WhatsApp%20Image%202026-04-03%20at%2021.15.12.jpeg"
    },
    {
      headline: "Learn to dive with the best",
      subheading: "ISC certified instruction from passionate divers who love what they do",
      cta_text: "View Courses",
      cta_link: "/courses",
      image: adminHero.images?.[3] || "https://customer-assets.emergentagent.com/job_3cfeeb3d-4da8-4519-ad3c-147d6848a7d7/artifacts/g426s0ty_WhatsApp%20Image%202026-04-03%20at%2021.15.13.jpeg"
    }
  ];

  useEffect(() => {
    axios.get(`${API_URL}/homepage`)
      .then(res => {
        setContent(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching homepage content:', err);
        setLoading(false);
      });
  }, []);

  // Hero slide auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroSlide(prev => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroSlides.length]);

  // Featured products auto-scroll
  useEffect(() => {
    if (!featuredRef.current || !content?.featured_products?.length) return;
    const interval = setInterval(() => {
      if (featuredRef.current) {
        const maxScroll = featuredRef.current.scrollWidth - featuredRef.current.clientWidth;
        if (featuredScrollPosition >= maxScroll - 10) {
          featuredRef.current.scrollTo({ left: 0, behavior: 'smooth' });
          setFeaturedScrollPosition(0);
        } else {
          featuredRef.current.scrollBy({ left: 300, behavior: 'smooth' });
          setFeaturedScrollPosition(prev => prev + 300);
        }
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [content?.featured_products?.length, featuredScrollPosition]);

  const scrollFeatured = useCallback((direction) => {
    if (!featuredRef.current) return;
    const scrollAmount = direction === 'left' ? -300 : 300;
    featuredRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setFeaturedScrollPosition(prev => Math.max(0, prev + scrollAmount));
  }, []);

  const scrollDivevolk = useCallback((direction) => {
    if (!divevolkRef.current) return;
    const scrollAmount = direction === 'left' ? -300 : 300;
    divevolkRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0EFEA]">
        <div className="w-12 h-12 border-4 border-[#0A2342] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const hero = content?.hero || {};
  const categories = content?.categories?.items || [];
  const trust = content?.trust?.items || [];
  const social = content?.social || {};
  const divevolk = content?.divevolk || {};
  const featuredProducts = content?.featured_products || [];
  const divevolkProducts = divevolk.products || [];
  
  // Get current hero slide
  const currentSlide = heroSlides[currentHeroSlide];

  return (
    <div className="bg-white">
      {/* Sign-up promo banner — anonymous visitors only */}
      <SignupBanner />

      {/* Floating closable promo ad — audience-targeted via Admin → Popups */}
      <FloatingAd />

      {/* HERO SECTION - Full-width with rotating content */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Images with Crossfade */}
        {heroSlides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 z-0 bg-cover bg-center transition-opacity duration-1000 ${
              idx === currentHeroSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `linear-gradient(to right, rgba(10, 35, 66, 0.9) 0%, rgba(10, 35, 66, 0.5) 50%, rgba(10, 35, 66, 0.2) 100%), url('${slide.image}')`
            }}
          />
        ))}
        
        {/* Hero Content - Animated */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full py-20">
          <div className="max-w-2xl">
            <h1 
              key={currentHeroSlide}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-heading font-bold text-white mb-6 leading-tight animate-fade-in"
            >
              {currentSlide.headline}
            </h1>
            <p 
              key={`sub-${currentHeroSlide}`}
              className="text-lg sm:text-xl text-white/90 mb-8 max-w-xl animate-fade-in"
              style={{ animationDelay: '0.1s' }}
            >
              {currentSlide.subheading}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={currentSlide.cta_link}>
                <Button 
                  size="lg"
                  className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-xl hover:shadow-2xl transition-all duration-300 text-base sm:text-lg h-14 px-8"
                  data-testid="hero-cta-btn"
                >
                  {currentSlide.cta_text}
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
              </Link>
              <Link to="/products">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A2342] text-base sm:text-lg h-14 px-8"
                >
                  View All Gear
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Hero Slide Indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {heroSlides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentHeroSlide(idx)}
                className={`h-3 rounded-full transition-all ${
                  idx === currentHeroSlide ? 'bg-[#FF6B35] w-8' : 'bg-white/50 hover:bg-white/80 w-3'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS CAROUSEL */}
      {featuredProducts.length > 0 && (
        <section className="py-16 sm:py-20 bg-white" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">
                Featured Products
              </h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => scrollFeatured('left')}
                  className="rounded-full border-[#0A2342] text-[#0A2342] hover:bg-[#0A2342] hover:text-white"
                  data-testid="featured-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => scrollFeatured('right')}
                  className="rounded-full border-[#0A2342] text-[#0A2342] hover:bg-[#0A2342] hover:text-white"
                  data-testid="featured-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <div
              ref={featuredRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {featuredProducts.map((product, idx) => {
                // Determine the correct link based on product type
                // Brand products have brand_id, or have images array instead of image_url
                const isBrandProduct = product.brand_id || (product.images && !product.image_url);
                const isPreDived = product.category === 'pre-owned';
                
                const productLink = isBrandProduct 
                  ? `/brand/divevolk` // Brand products link to brand page
                  : isPreDived
                    ? `/pre-dived` // Pre-dived products link to pre-dived page
                    : `/products/${product.id}`; // Regular products
                
                return (
                  <Link
                    key={product.id || idx}
                    to={productLink}
                    className="flex-shrink-0 w-72 snap-start group"
                    data-testid={`featured-product-${product.id}`}
                  >
                    <div className="bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={product.image_url || product.images?.[0] || '/placeholder.jpg'}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="font-heading font-semibold text-[#0A2342] text-lg mb-3 line-clamp-2 group-hover:text-[#FF6B35] transition-colors">
                          {product.name}
                        </h3>
                        <Button
                          variant="outline"
                          className="w-full border-[#0A2342] text-[#0A2342] hover:bg-[#0A2342] hover:text-white"
                        >
                          View Product
                        </Button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SHOP BY CATEGORY */}
      {categories.length > 0 && (
        <section className="py-12 sm:py-20 bg-[#F0EFEA]" data-testid="categories-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <h2 className="text-2xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-6 sm:mb-10 text-center">
              Shop by Category
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
              {categories.sort((a, b) => a.order - b.order).map((cat, idx) => (
                <Link
                  key={cat.id || idx}
                  to={cat.link || `/products?category=${cat.title?.toLowerCase()}`}
                  className="group block rounded-xl sm:rounded-2xl overflow-hidden"
                  data-testid={`category-${cat.id}`}
                >
                  <div className="relative aspect-square bg-[#0A2342] flex items-center justify-center">
                    {cat.image_url && (
                      <img
                        src={cat.image_url}
                        alt={cat.title}
                        className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-30 group-hover:scale-110 transition-all duration-500"
                      />
                    )}
                    <span className="relative z-10 text-white font-heading font-bold text-base sm:text-lg text-center px-3 py-2">
                      {cat.title}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DIVEVOLK FEATURE SECTION */}
      <section className="relative py-12 sm:py-24 overflow-hidden" data-testid="divevolk-section">
        {/* Background with DiveVolk product image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: divevolkProducts[0]?.images?.[0] 
              ? `url('${divevolkProducts[0].images[0]}')`
              : `url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920')`
          }}
        />
        <div className="absolute inset-0 bg-[#0A2342]/90" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          {/* Mobile: Stack vertically, Desktop: Side by side */}
          <div className="space-y-8 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center lg:space-y-0">
            {/* Text Content - Always left aligned */}
            <div className="text-left">
              <span className="inline-block bg-[#FF6B35] text-white text-xs sm:text-sm font-medium px-3 sm:px-4 py-1 rounded-full mb-4">
                FEATURED BRAND
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                {divevolk.title || "Turn your phone into an underwater camera"}
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-white/80 mb-6 sm:mb-8">
                {divevolk.subtitle || "Premium underwater housings for your smartphone"}
              </p>
              <Link to={divevolk.cta_link || "/brand/divevolk"}>
                <Button 
                  size="lg"
                  className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-xl text-sm sm:text-base lg:text-lg h-11 sm:h-12 lg:h-14 px-6 sm:px-8 lg:px-10"
                  data-testid="divevolk-cta"
                >
                  {divevolk.cta_text || "Explore DiveVolk"}
                  <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
            </div>
            
            {/* DiveVolk Products Grid */}
            {divevolkProducts.length > 0 && (
              <div className="relative">
                <div className="flex gap-2 justify-end mb-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => scrollDivevolk('left')}
                    className="rounded-full border-white/30 text-white hover:bg-white hover:text-[#0A2342]"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => scrollDivevolk('right')}
                    className="rounded-full border-white/30 text-white hover:bg-white hover:text-[#0A2342]"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
                
                <div
                  ref={divevolkRef}
                  className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {divevolkProducts.map((product, idx) => (
                    <Link
                      key={product.id || idx}
                      to={`/brand/divevolk`}
                      className="flex-shrink-0 w-56 snap-start group"
                      data-testid={`divevolk-product-${product.id}`}
                    >
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all duration-300 border border-white/10">
                        {idx === 0 && (
                          <div className="absolute top-3 left-3 z-10">
                            <span className="bg-[#FF6B35] text-white text-xs font-bold px-3 py-1 rounded-full">
                              BEST SELLER
                            </span>
                          </div>
                        )}
                        <div className="aspect-square overflow-hidden relative">
                          <img
                            src={product.images?.[0] || product.image_url || '/placeholder.jpg'}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading font-semibold text-white text-sm mb-2 line-clamp-2">
                            {product.name}
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full border-white/50 text-white hover:bg-white hover:text-[#0A2342]"
                          >
                            View Product
                          </Button>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHY JUST DIVE - TRUST STRIP */}
      {trust.length > 0 && (
        <section className="py-16 sm:py-20 bg-white" data-testid="trust-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-12 text-center">
              Why Just Dive?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {trust.sort((a, b) => a.order - b.order).map((item, idx) => {
                const IconComponent = iconMap[item.icon] || Shield;
                return (
                  <div 
                    key={item.id || idx} 
                    className="text-center group"
                    data-testid={`trust-item-${item.id}`}
                  >
                    <div className="w-16 h-16 mx-auto bg-[#F0EFEA] rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#FF6B35] transition-colors">
                      <IconComponent className="w-8 h-8 text-[#0A2342] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-semibold text-[#0A2342] text-xl mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* SOCIAL PROOF - CUSTOMER IMAGES & TESTIMONIALS */}
      {/* Social Proof / Testimonials */}
      {(social.images?.length > 0 || social.testimonials?.length > 0) && (
      <section className="py-16 sm:py-20 bg-[#F0EFEA]" data-testid="social-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-4 text-center">
            {social.title || "What Our Divers Say"}
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Real photos from our diving community
          </p>
          
          {/* Customer Images Grid */}
          {social.images?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {social.images.slice(0, 4).map((img, idx) => (
                <div 
                  key={idx} 
                  className="aspect-square rounded-2xl overflow-hidden group cursor-pointer"
                  data-testid={`social-image-${idx}`}
                >
                  <img
                    src={img}
                    alt={`Customer photo ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          )}
          
          {/* Testimonials */}
          {social.testimonials?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {social.testimonials.sort((a, b) => a.order - b.order).map((test, idx) => (
                <div 
                  key={test.id || idx}
                  className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow"
                  data-testid={`testimonial-${test.id}`}
                >
                  <Quote className="w-8 h-8 text-[#FF6B35] mb-4" />
                  <p className="text-gray-700 mb-4 italic">"{test.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0A2342] rounded-full flex items-center justify-center text-white font-bold">
                      {test.author?.[0] || 'A'}
                    </div>
                    <div>
                      <p className="font-semibold text-[#0A2342]">{test.author}</p>
                      <div className="flex gap-1">
                        {[...Array(test.rating || 5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#FF6B35] text-[#FF6B35]" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-[#0A2342] mb-6">
            Ready to Dive In?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of divers who trust Just Dive for their underwater adventures.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products">
              <Button 
                size="lg"
                className="bg-[#FF6B35] text-white hover:bg-[#FF6B35]/90 shadow-xl text-lg h-14 px-8"
              >
                Shop Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/membership">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-[#0A2342] text-[#0A2342] hover:bg-[#0A2342] hover:text-white text-lg h-14 px-8"
              >
                Join the Club
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* As featured on KAYAK */}
      <section className="bg-white py-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <KayakBadge />
        </div>
      </section>

      {/* Weekly Ocean Report subscribe */}
      <section className="bg-white py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
          <NewsletterSubscribe variant="hero" />
        </div>
      </section>

      {/* Social Links Footer */}
      <section className="bg-[#0A2342] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <span className="text-white/80 font-medium">Follow us:</span>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/JustdiveSA" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 px-5 py-2.5 rounded-full hover:bg-[#FF6B35] transition-colors text-white"
                data-testid="social-facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </a>
              <a 
                href="https://www.instagram.com/justdive.co.za" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/10 px-5 py-2.5 rounded-full hover:bg-[#FF6B35] transition-colors text-white"
                data-testid="social-instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                Instagram
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
